import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Link2, Unlink } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime, formatRelative } from '../lib/format';
import { invalidateInventory, useDebouncedValue } from '../lib/hooks';
import { DEVICE_FAMILY_LABEL } from '../lib/integrations';
import type { ManagedDeviceListItem, AssetDetail, Page } from '../lib/types';
import { AppleCareBadge, AsmBadge, MdmBadge } from './device-badges';
import { Combobox } from './pickers';
import { useToast } from './toast';
import { Button, Card, CardHeader, DescriptionList } from './ui';

function useRefresh() {
  const queryClient = useQueryClient();
  return () => {
    invalidateInventory(queryClient);
    void queryClient.invalidateQueries({ queryKey: ['managed-devices'] });
    void queryClient.invalidateQueries({ queryKey: ['managed-device'] });
  };
}

function LinkApplePicker({ assetId, serialNumber }: { assetId: string; serialNumber: string | null }) {
  const toast = useToast();
  const refresh = useRefresh();
  const [query, setQuery] = useState(serialNumber ?? '');
  const search = useDebouncedValue(query.trim(), 250);
  const results = useQuery({
    queryKey: ['managed-devices', 'picker', search],
    queryFn: () => api.get<Page<ManagedDeviceListItem>>('/managed-devices', { search, linked: 'false', pageSize: 8 }),
    enabled: search.length > 0,
  });
  const link = useMutation({
    mutationFn: (managedDeviceId: string) => api.put(`/assets/${assetId}/apple-device`, { managedDeviceId }),
    onSuccess: () => {
      refresh();
      toast.success('Linked to device');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Combobox
      inline
      query={query}
      onQueryChange={setQuery}
      loading={results.isFetching || link.isPending}
      items={search ? (results.data?.items ?? []) : []}
      getKey={(d) => d.id}
      placeholder="Search unlinked managed devices by serial, name, or user"
      emptyText="No unlinked devices match"
      onSelect={(device) => link.mutate(device.id)}
      renderItem={(device) => (
        <div className="min-w-0">
          <p className="font-mono text-xs font-semibold text-slate-900">{device.serialNumber}</p>
          <p className="truncate text-xs text-slate-500">{[device.model, device.name, device.assignedUserName].filter(Boolean).join(' · ')}</p>
        </div>
      )}
    />
  );
}

/** The Mac, iPad, or Apple TV an asset is, as Jamf and Apple School Manager report it. */
export function ManagedDeviceCard({ asset }: { asset: AssetDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const refresh = useRefresh();
  const [linking, setLinking] = useState(false);
  const device = asset.managedDevice;
  const canWrite = can('TECHNICIAN');

  const unlink = useMutation({
    mutationFn: () => api.put(`/assets/${asset.id}/apple-device`, { managedDeviceId: null }),
    onSuccess: () => {
      refresh();
      toast.success('Unlinked device');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <CardHeader
        title="Managed device"
        actions={
          device && (
            <Link to={`/managed-devices/${device.id}`} className="inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:text-brand-800">
              Open <ExternalLink className="size-3.5" />
            </Link>
          )
        }
      />
      <div className="p-5">
        {device ? (
          <div className="space-y-4">
            <DescriptionList
              columns={1}
              items={[
                { label: 'Device', value: [DEVICE_FAMILY_LABEL[device.productFamily], device.name].filter(Boolean).join(' · ') },
                { label: 'Management', value: <MdmBadge device={device} /> },
                {
                  label: 'Last check-in',
                  value: device.mdmProvider && device.lastCheckInAt && `${formatRelative(device.lastCheckInAt)} · ${formatDateTime(device.lastCheckInAt)}`,
                },
                { label: 'Assigned user', value: device.assignedUserName ?? device.assignedUserEmail },
                { label: 'Operating system', value: device.mdmProvider && [device.osName, device.osVersion].filter(Boolean).join(' ') },
                { label: 'Battery', value: device.batteryLevel !== null ? `${device.batteryLevel}%` : null },
                { label: 'Apple School Manager', value: device.asmSeenAt || device.releasedFromOrgAt ? <AsmBadge device={device} /> : null },
                { label: 'Coverage', value: device.appleCareEndsAt ? <AppleCareBadge endsAt={device.appleCareEndsAt} description={device.appleCareDescription} /> : null },
              ]}
            />
            {canWrite && (
              <Button size="sm" variant="ghost" icon={<Unlink />} loading={unlink.isPending} onClick={() => unlink.mutate()}>
                Unlink device
              </Button>
            )}
          </div>
        ) : canWrite && linking ? (
          <LinkApplePicker assetId={asset.id} serialNumber={asset.serialNumber} />
        ) : (
          <div className="text-sm text-slate-600">
            <p>Not linked to a device from your MDM or Apple School Manager.</p>
            {canWrite && (
              <Button size="sm" className="mt-3" icon={<Link2 />} onClick={() => setLinking(true)}>
                Link a device
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
